'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper/core';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

import { FreeMode, Navigation, Pagination, Thumbs } from 'swiper/modules';
import Image from 'next/image';
import FsLightBox from './FsLightBox';
import Media from './Media';
import { GalleryItem } from '@/types/product_page/productType';
import { generateDirectusSrc } from '@/helpers/image';
import styles from './gallery.module.css';

export default function ImageGallery({ data }: { data: GalleryItem[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showZoom, setShowZoom] = useState(false);
  const [clicked, setClicked] = useState(0);
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const mainSwiperRef = useRef<SwiperCore | null>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [scrolling, setScrolling] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollTop(window.scrollY);
      setTimeout(() => {
        setScrolling(window.scrollY > scrollTop);
      }, 700);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollTop]);

  const handleMainSlideChange = () => {
    if (mainSwiperRef.current) {
      const newIndex = mainSwiperRef.current.realIndex;
      setActiveIndex(newIndex);
    }
  };

  useEffect(() => {
    if (showZoom && document !== undefined) {
      document.body.style.overflow = 'hidden'; // Apply overflow: hidden directly
    } else {
      document.body.style.overflow = ''; // Remove overflow: hidden when not needed
    }

    return () => {
      document.body.style.overflow = ''; // Ensure overflow: hidden is removed when component unmounts
    };
  }, [showZoom]);

  const imageSize = (type: string | null) => {
    if (type === 'image_contain' || type === 'Image Contain') {
      return 'object-contain';
    }
    if (type === 'image_cover') {
      return 'object-cover';
    }
    if (type === 'image_fill') {
      return 'object-fill';
    }
  };

  useEffect(() => {
    if (!data?.length) return;
    const preloadImages = () => {
      data.slice(0, 3).forEach((item) => {
        const id = item?.layout_asset_translations_id?.item?.id;
        const type = item?.layout_asset_translations_id?.type;
        if (id && type !== 'video') {
          const img = new window.Image();
          img.src = generateDirectusSrc(id);
        }
      });
    };

    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(preloadImages);
      } else {
        setTimeout(preloadImages, 300);
      }
    }
  }, [data]);

  const handleSelectSlide = (idx: number) => {
    if (mainSwiperRef?.current) {
      mainSwiperRef?.current?.slideToLoop(idx);
    }

    if (thumbnailRefs?.current[idx]) {
      thumbnailRefs?.current[idx]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  return (
    <div className="md:w-full lg:w-1/2  min-w-[50%] -mx-4 md:mx-unset">
      {data?.length > 0 && (
        <FsLightBox
          clicked={clicked}
          media={data}
          show={showZoom}
          setShow={setShowZoom}
        />
      )}

      <div className="md:hidden " style={{ position: 'sticky', top: '64px' }}>
        <Swiper
          loop={true}
          rewind={false}
          slidesPerView={1}
          // centeredSlides={true}
          spaceBetween={0}
          thumbs={{ swiper: thumbsSwiper }}
          modules={[Navigation, Thumbs, Pagination]}
          onSwiper={(swiper) => (mainSwiperRef.current = swiper)}
          onSlideChange={handleMainSlideChange}
          className={styles.mainSwiper}
        >
          {data?.length ? (
            data?.map((item, index) => (
              <SwiperSlide key={index}>
                {item?.layout_asset_translations_id?.type === 'video' ? (
                  <video className={`w-auto h-auto`} controls>
                    <source
                      src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${item?.layout_asset_translations_id?.item?.id}`}
                      type="video/ogg"
                    />
                  </video>
                ) : (
                  <Image
                    src={generateDirectusSrc(
                      item?.layout_asset_translations_id?.item?.id
                    )}
                    alt={
                      item?.layout_asset_translations_id?.item?.title ||
                      'Product image'
                    }
                    title={item?.layout_asset_translations_id?.item?.title}
                    width={480}
                    height={480}
                    sizes="(max-width: 768px) 100vw, 480px"
                    priority={index < 4}
                    unoptimized={true}
                    className={`w-full h-full ${imageSize(item?.layout_asset_translations_id?.type)}`}
                  />
                )}
              </SwiperSlide>
            ))
          ) : (
            <SwiperSlide className="rounded-xl">
              <div className="col-span-2 w-full h-80 flex flex-col gap-1 items-center opacity-50 justify-center">
                <Image
                  src={'/assets/loader/no-image.png'}
                  width={500}
                  height={500}
                  quality={75}
                  priority={true}
                  className="w-7 h-7"
                  alt="img"
                />
                <p className="font-proxima_font text-sm">No images</p>
              </div>
            </SwiperSlide>
          )}
        </Swiper>

        <div className="w-full flex items-center no-scrollbar p-1 overflow-x-auto snap-x snap-mandatory justify-start px-2 gap-[6px] mt-2">
          {data?.map((item, idx) => {
            return (
              <div
                key={item?.layout_asset_translations_id?.id}
                ref={(el: HTMLDivElement | null) => {
                  thumbnailRefs.current[idx] = el;
                }}
                onClick={() => {
                  handleSelectSlide(idx);
                }}
                onMouseEnter={() => {
                  handleSelectSlide(idx);
                }}
                className={`w-[64px] h-[49px] bg-[#ebebeb] overflow-hidden transition-all outline-2 outline duration-300 ease-in-out outline-offset-1 shrink-0 ${activeIndex === idx ? 'border-white outline-black' : 'border-[#dededb] outline-transparent'} border-2 rounded-[4px]`}
              >
                <Image
                  src={generateDirectusSrc(
                    item?.layout_asset_translations_id?.item?.id
                  )}
                  alt={
                    item?.layout_asset_translations_id?.item?.title ||
                    'Thumbnail'
                  }
                  title={item?.layout_asset_translations_id?.item?.title}
                  width={64}
                  height={49}
                  sizes="64px"
                  priority={idx < 4}
                  unoptimized={true}
                  className={`w-full h-full ${imageSize(item?.layout_asset_translations_id?.type)}`}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div
        className={`grid-cols-1 sticky top-0 md:grid-cols-2 ${scrolling && window.scrollY > 700 ? 'top-8' : 'top-20'}  gap-[10px] md:grid hidden overflow-auto`}
      >
        {data?.length > 0 ? (
          data?.map((item, index) => (
            <div
              key={index}
              className={` relative ${
                item?.layout_asset_translations_id?.colspan === 12
                  ? 'col-span-2 h-[490px]'
                  : 'col-span-1'
              } `}
              onMouseEnter={() => {
                const id = item?.layout_asset_translations_id?.item?.id;
                if (
                  id &&
                  item?.layout_asset_translations_id?.type !== 'video' &&
                  typeof window !== 'undefined'
                ) {
                  const img = new window.Image();
                  img.src = generateDirectusSrc(id);
                }
              }}
            >
              <div
                onClick={() => {
                  setClicked(index);
                  setShowZoom(true);
                }}
                className="w-full h-full hover:border-2 cursor-zoom-in absolute rounded-[10px]"
              ></div>

              <Media
                type={item?.layout_asset_translations_id?.type}
                file={item?.layout_asset_translations_id?.item}
                index={index}
                colspan={item?.layout_asset_translations_id?.colspan}
              />
            </div>
          ))
        ) : (
          <div className="col-span-2 w-full h-80 flex flex-col gap-1 items-center opacity-50 justify-center">
            <Image
              src={'/assets/loader/no-image.png'}
              width={500}
              height={500}
              quality={100}
              priority={true}
              className="w-7 h-7"
              alt="img"
            />
            <p className="font-proxima_font text-sm">No image</p>
          </div>
        )}
      </div>
    </div>
  );
}
